import { Filter, Pagination, Search, Sort } from "@/components/Table/Table";
import { IImpact, ImpactMetricKey } from "@/pages/dashboard/utils/types";
import { dateFormat, useDateRange } from "@/pages/dashboard/hooks/useDateRange";
import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import { filterSearchToGetFilter, sortToParam } from "@/utilities/api";
import { Result } from "@/api/types";

interface GetPackagingImpactTotals {
  packaging_impact: IImpact;
  company_impact: IImpact;
}

export function usePackagingImpactTotals({ metrics }: { metrics: ImpactMetricKey[] }) {
  const { startDate, endDate } = useDateRange();

  const query = useQuery({
    queryKey: ["dashboard", "packaging", "packagingImpactTotals", startDate, endDate, metrics],
    enabled: !!startDate && !!endDate,
    queryFn: () =>
      axios
        .get<GetPackagingImpactTotals>("/dashboard/packaging/totals", {
          params: {
            end_date: endDate?.format(dateFormat),
            start_date: startDate?.format(dateFormat),
            metrics: metrics.join(";"),
          },
        })
        .then((result) => result.data),
  });

  return query;
}

export type MaterialGroupBy = "id" | "name";

export interface IPackagingMaterialImpactPerMaterial {
  packaging_material_id: number;
  packaging_material_name: string;
  suppliers: { name: string | null; address: string | null }[];
  unit: string;
  quantity: number;
  category: string[];
  impact: IImpact;
}

export interface GetPackagingMaterialImpactPerMaterial {
  total_rows: number;
  data: IPackagingMaterialImpactPerMaterial[];
  total_material_impact: IImpact;
}

export function usePackagingImpactPerMaterial({
  pagination,
  filter,
  search,
  sort,
  metric,
  groupBy = "id",
}: {
  pagination?: Pagination;
  filter?: Filter;
  search?: Search;
  sort?: Sort;
  metric: ImpactMetricKey;
  groupBy?: MaterialGroupBy;
}) {
  const { startDate, endDate } = useDateRange();

  const query = useQuery({
    queryKey: [
      "dashboard",
      "packaging",
      "packagingImpactPerMaterial",
      startDate,
      endDate,
      pagination,
      filter,
      search,
      sort,
      groupBy,
      metric,
    ],
    enabled: !!startDate && !!endDate,
    queryFn: () =>
      axios
        .get<GetPackagingMaterialImpactPerMaterial>("/dashboard/packaging/per_material", {
          params: {
            page: pagination?.page === undefined ? 1 : pagination.page,
            page_size: pagination?.pageSize || 50,
            ...filterSearchToGetFilter(filter, search),
            ...sortToParam(sort),
            end_date: endDate?.format(dateFormat),
            start_date: startDate?.format(dateFormat),
            group_by: groupBy,
            metric,
          },
        })
        .then((result) => result.data),
  });

  return query;
}

export function usePackagingImpactPerMaterialExport({
  pagination,
  filter,
  search,
  sort,
  groupBy,
  metric,
}: {
  pagination?: Pagination;
  filter?: Filter;
  search?: Search;
  sort?: Sort;
  groupBy?: MaterialGroupBy;
  metric: ImpactMetricKey;
}) {
  const { startDate, endDate } = useDateRange();

  return useMutation({
    mutationFn: () =>
      axios
        .get<Result<{ url: string }>>("/dashboard/packaging/per_material", {
          params: {
            page: pagination?.page === undefined ? 1 : pagination.page,
            page_size: pagination?.pageSize || 50,
            ...filterSearchToGetFilter(filter, search),
            ...sortToParam(sort),
            end_date: endDate?.format(dateFormat),
            start_date: startDate?.format(dateFormat),
            export: true,
            group_by: groupBy,
            metric,
          },
        })
        .then((result) => result.data.result.url),
  });
}

interface GetPackagingImpactPerType extends Result {
  label: string;
  impact: IImpact | null;
}

export function usePackagingImpactPerType({ metric }: { metric: ImpactMetricKey }) {
  const { startDate, endDate } = useDateRange();

  const query = useQuery({
    queryKey: ["dashboard", "packaging", "packagingImpactPerType", startDate, endDate, metric],
    enabled: !!startDate && !!endDate,
    queryFn: () =>
      axios
        .get<GetPackagingImpactPerType[]>("/dashboard/packaging/product_vs_logistics", {
          params: {
            end_date: endDate?.format(dateFormat),
            start_date: startDate?.format(dateFormat),
            metrics: metric,
          },
        })
        .then((result) => result.data),
  });

  return query;
}
